import {
  type ChartIndexRange,
  ChartPlotInteractionRect,
  ChartRangeResetButton,
  ChartRangeSelectionOverlay,
  SharedHoverTooltip,
  usePlotPointerInteraction,
} from "@/components/charts/svg-chart-interactions";
import type {
  AttomTaxAssessorExtractDateSummary,
  AttomTaxAssessorStateOverTime,
} from "@/hera-api/attom-tax-assessor-api";
import { useCallback, useMemo } from "react";

type ChartSeries = {
  label: string;
  color: string;
  values: number[];
  dashed?: boolean;
};

type ChartLayout = {
  n: number;
  left: number;
  right: number;
  top: number;
  plotH: number;
  plotW: number;
  plotRight: number;
  baselineY: number;
  svgW: number;
  svgH: number;
  tickFont: number;
  tickRotate: number;
  xAt: (i: number) => number;
};

const AXIS_LABEL_THEME = {
  yValuePad: 6,
  yTitleXOffset: 40,
  xTickBelowBaseline: 5,
  yTitleSize: 5.25,
  xTitleSize: 6,
  xTitleYFromBottom: 8,
  tickFontScale: 0.82,
} as const;

function buildChartLayout(pointCount: number): ChartLayout {
  const n = Math.max(1, pointCount);
  const left = 86;
  const rightMargin = 10;
  const top = 2;
  const plotH = 78;
  const baselineY = top + plotH;
  const targetWidth = 640;
  const seg = n <= 1 ? targetWidth : Math.max(16, targetWidth / (n - 1));
  const plotW = n <= 1 ? targetWidth : (n - 1) * seg;
  const plotRight = left + plotW;
  const bottomPad = 66;
  const tickBase = n > 20 ? 5.75 : n > 14 ? 6.25 : 6.75;
  return {
    n,
    left,
    right: rightMargin,
    top,
    plotH,
    plotW,
    plotRight,
    baselineY,
    svgW: plotRight + rightMargin,
    svgH: baselineY + bottomPad,
    tickFont: tickBase,
    tickRotate: -34,
    xAt: (i: number) =>
      n <= 1 ? left + plotW / 2 : left + (i * plotW) / (n - 1),
  };
}

function buildAxisTicks(maxValue: number): number[] {
  const tickCount = 3;
  const safeMax = Math.max(1, maxValue);
  const rawStep = safeMax / (tickCount - 1);
  const stepPow = 10 ** Math.floor(Math.log10(rawStep));
  const stepNorm = rawStep / stepPow;
  const stepBase =
    stepNorm <= 1 ? 1 : stepNorm <= 2 ? 2 : stepNorm <= 5 ? 5 : 10;
  const step = stepBase * stepPow;
  const niceMax = Math.ceil(safeMax / step) * step;
  return Array.from(
    { length: Math.round(niceMax / step) + 1 },
    (_, i) => i * step,
  );
}

export function formatExtractDateLabel(extractDate: string): string {
  const [, month, day] = extractDate.split("-");
  if (!month || !day) {
    return extractDate;
  }
  return `${month}/${day}`;
}

function stateColor(index: number): string {
  return `hsl(${(index * 47) % 360} 65% 45%)`;
}

export function buildStateSeries(
  rows: AttomTaxAssessorStateOverTime[],
  extractDates: string[],
): ChartSeries[] {
  const states = [...new Set(rows.map((row) => row.state))].sort();
  const rowByKey = new Map<string, AttomTaxAssessorStateOverTime>();
  for (const row of rows) {
    rowByKey.set(`${row.extract_date}|${row.state}`, row);
  }

  return states.map((state, index) => ({
    label: state,
    color: stateColor(index),
    values: extractDates.map(
      (extractDate) =>
        rowByKey.get(`${extractDate}|${state}`)?.record_count ?? 0,
    ),
  }));
}

export function buildStateJurisdictionSeries(
  rows: AttomTaxAssessorStateOverTime[],
  extractDates: string[],
): ChartSeries[] {
  const states = [...new Set(rows.map((row) => row.state))].sort();
  const rowByKey = new Map<string, AttomTaxAssessorStateOverTime>();
  for (const row of rows) {
    rowByKey.set(`${row.extract_date}|${row.state}`, row);
  }

  return states.map((state, index) => ({
    label: state,
    color: stateColor(index),
    values: extractDates.map(
      (extractDate) =>
        rowByKey.get(`${extractDate}|${state}`)?.jurisdiction_count ?? 0,
    ),
  }));
}

function AttomLineChart({
  title,
  description,
  xLabels,
  xTooltipLabels,
  yAxisLabel,
  series,
  idPrefix,
  onRangeSelect,
  maxTooltipRows,
}: {
  title: string;
  description: string;
  xLabels: string[];
  xTooltipLabels?: string[];
  yAxisLabel: string;
  series: ChartSeries[];
  idPrefix: string;
  onRangeSelect?: (range: ChartIndexRange) => void;
  maxTooltipRows?: number;
}) {
  const chart = useMemo(
    () => buildChartLayout(xLabels.length),
    [xLabels.length],
  );
  const tooltipLabels = xTooltipLabels ?? xLabels;
  const maxValue = useMemo(
    () =>
      Math.max(1, ...series.flatMap((entry) => entry.values.slice(0, chart.n))),
    [chart.n, series],
  );
  const yTicks = useMemo(() => buildAxisTicks(maxValue), [maxValue]);
  const yMax = yTicks[yTicks.length - 1] ?? 1;
  const toY = useCallback(
    (value: number) => chart.baselineY - (value / yMax) * (chart.plotH - 4),
    [chart.baselineY, chart.plotH, yMax],
  );
  const pointsFor = (values: number[]) =>
    values
      .slice(0, chart.n)
      .map((value, index) => `${chart.xAt(index)},${toY(value)}`)
      .join(" ");
  const plotInteraction = usePlotPointerInteraction(chart, { onRangeSelect });
  const hoverSeries = useMemo(
    () =>
      series.map((entry) => ({
        label: entry.label,
        color: entry.color,
        valueAt: (index: number) => entry.values[index] ?? 0,
        yAt: (index: number) => toY(entry.values[index] ?? 0),
      })),
    [series, toY],
  );

  if (xLabels.length === 0 || series.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-3">
        <h3 className="mb-1.5 text-sm font-medium text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground">No extract data yet.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card p-3">
      <h3 className="mb-1.5 text-sm font-medium text-foreground">{title}</h3>
      <p className="mb-1 text-xs text-muted-foreground">{description}</p>
      <svg
        viewBox={`0 0 ${chart.svgW} ${chart.svgH}`}
        className="block h-auto w-full min-h-0 text-foreground"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={title}
      >
        <text
          x={Math.max(4, chart.left - AXIS_LABEL_THEME.yTitleXOffset)}
          y={chart.top + chart.plotH / 2}
          textAnchor="middle"
          className="fill-muted-foreground"
          style={{ fontSize: AXIS_LABEL_THEME.yTitleSize }}
          transform={`rotate(-90, ${Math.max(4, chart.left - AXIS_LABEL_THEME.yTitleXOffset)}, ${chart.top + chart.plotH / 2})`}
        >
          {yAxisLabel}
        </text>
        <line
          x1={chart.left}
          y1={chart.baselineY}
          x2={chart.plotRight}
          y2={chart.baselineY}
          stroke="currentColor"
          className="text-border"
          strokeWidth="1"
        />
        {yTicks.map((tick) => {
          const yy = toY(tick);
          return (
            <g key={`${idPrefix}-y-${String(tick)}`}>
              <line
                x1={chart.left}
                y1={yy}
                x2={chart.plotRight}
                y2={yy}
                stroke="currentColor"
                className="text-border/70"
                strokeWidth="0.5"
                strokeDasharray="2 3"
              />
              <text
                x={chart.left - AXIS_LABEL_THEME.yValuePad}
                y={yy + 2.5}
                textAnchor="end"
                className="fill-muted-foreground text-[6px]"
              >
                {tick.toLocaleString()}
              </text>
            </g>
          );
        })}
        {Array.from({ length: chart.n }, (_, index) => (
          <line
            key={`${idPrefix}-x-grid-${String(index)}`}
            x1={chart.xAt(index)}
            y1={chart.top}
            x2={chart.xAt(index)}
            y2={chart.baselineY}
            stroke="currentColor"
            className="text-border/45"
            strokeWidth="0.5"
            strokeDasharray="2 4"
          />
        ))}
        {series.map((entry) => (
          <polyline
            key={`${idPrefix}-series-${entry.label}`}
            fill="none"
            stroke={entry.color}
            strokeWidth={series.length > 8 ? "1.5" : "2"}
            strokeDasharray={entry.dashed ? "4 2" : undefined}
            points={pointsFor(entry.values)}
            pointerEvents="none"
          />
        ))}
        {xLabels.slice(0, chart.n).map((label, index) => (
          <g
            key={`${idPrefix}-x-${label}-${String(index)}`}
            transform={`translate(${chart.xAt(index)} ${chart.baselineY + AXIS_LABEL_THEME.xTickBelowBaseline})`}
            pointerEvents="none"
          >
            <text
              transform={`rotate(${chart.tickRotate})`}
              textAnchor="end"
              className="fill-muted-foreground"
              style={{
                fontSize: chart.tickFont * AXIS_LABEL_THEME.tickFontScale,
              }}
            >
              {label}
            </text>
          </g>
        ))}
        <text
          x={(chart.left + chart.plotRight) / 2}
          y={chart.svgH - AXIS_LABEL_THEME.xTitleYFromBottom}
          textAnchor="middle"
          className="fill-muted-foreground"
          style={{ fontSize: AXIS_LABEL_THEME.xTitleSize }}
          pointerEvents="none"
        >
          Extract date
        </text>
        <ChartRangeSelectionOverlay
          chart={chart}
          previewRange={plotInteraction.previewRange}
        />
        <ChartPlotInteractionRect chart={chart} interaction={plotInteraction} />
        {plotInteraction.hoverIndex !== null && !plotInteraction.isDragging ? (
          <SharedHoverTooltip
            chart={chart}
            xLabel={tooltipLabels[plotInteraction.hoverIndex] ?? ""}
            hoverIndex={plotInteraction.hoverIndex}
            series={hoverSeries}
            valueFormatter={(value) => Math.round(value).toLocaleString()}
            maxSeriesRows={maxTooltipRows}
          />
        ) : null}
      </svg>
      <div className="mt-1.5 flex max-h-24 flex-wrap gap-x-3 gap-y-0.5 overflow-y-auto text-xs text-muted-foreground">
        {series.map((entry) => (
          <span
            key={`${idPrefix}-legend-${entry.label}`}
            className="flex items-center gap-1.5"
          >
            <span
              className="h-2 w-4 rounded-sm"
              style={{ background: entry.color }}
              aria-hidden
            />
            <span>{entry.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export function AttomExtractHistoryChart({
  rows,
  onRangeSelect,
}: {
  rows: AttomTaxAssessorExtractDateSummary[];
  onRangeSelect?: (range: ChartIndexRange) => void;
}) {
  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => a.extract_date.localeCompare(b.extract_date)),
    [rows],
  );
  const xLabels = useMemo(
    () => sortedRows.map((row) => formatExtractDateLabel(row.extract_date)),
    [sortedRows],
  );
  const xTooltipLabels = useMemo(
    () => sortedRows.map((row) => row.extract_date),
    [sortedRows],
  );
  const series = useMemo<ChartSeries[]>(
    () => [
      {
        label: "Records",
        color: "hsl(var(--primary))",
        values: sortedRows.map((row) => row.record_count),
      },
      {
        label: "Jurisdictions",
        color: "hsl(var(--muted-foreground))",
        values: sortedRows.map((row) => row.jurisdiction_count),
        dashed: true,
      },
    ],
    [sortedRows],
  );

  return (
    <AttomLineChart
      idPrefix="attom-extract-history"
      title="Extract history"
      description="Total records and jurisdictions across scanned extract dates. Drag to filter the date range."
      xLabels={xLabels}
      xTooltipLabels={xTooltipLabels}
      yAxisLabel="Count"
      series={series}
      onRangeSelect={onRangeSelect}
    />
  );
}

export function AttomRecordsByStateChart({
  rows,
  extractDates,
  onRangeSelect,
}: {
  rows: AttomTaxAssessorStateOverTime[];
  extractDates: string[];
  onRangeSelect?: (range: ChartIndexRange) => void;
}) {
  const xLabels = useMemo(
    () =>
      extractDates.map((extractDate) => formatExtractDateLabel(extractDate)),
    [extractDates],
  );
  const series = useMemo(
    () => buildStateSeries(rows, extractDates),
    [extractDates, rows],
  );

  return (
    <AttomLineChart
      idPrefix="attom-records-by-state"
      title="Records by state"
      description="Record counts per state across scanned extract dates. Drag to filter the date range."
      xLabels={xLabels}
      xTooltipLabels={extractDates}
      yAxisLabel="Records"
      series={series}
      onRangeSelect={onRangeSelect}
      maxTooltipRows={12}
    />
  );
}

export function AttomJurisdictionsByStateChart({
  rows,
  extractDates,
  onRangeSelect,
}: {
  rows: AttomTaxAssessorStateOverTime[];
  extractDates: string[];
  onRangeSelect?: (range: ChartIndexRange) => void;
}) {
  const xLabels = useMemo(
    () =>
      extractDates.map((extractDate) => formatExtractDateLabel(extractDate)),
    [extractDates],
  );
  const series = useMemo(
    () => buildStateJurisdictionSeries(rows, extractDates),
    [extractDates, rows],
  );

  return (
    <AttomLineChart
      idPrefix="attom-jurisdictions-by-state"
      title="Jurisdictions by state"
      description="Jurisdiction counts per state across scanned extract dates. Drag to filter the date range."
      xLabels={xLabels}
      xTooltipLabels={extractDates}
      yAxisLabel="Jurisdictions"
      series={series}
      onRangeSelect={onRangeSelect}
      maxTooltipRows={12}
    />
  );
}

export { ChartRangeResetButton };
